import { ECreditApplicationStatus, EDecision, EOriginSystemId } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Action, ActionList, Banner, useCan, useConfirmDialog } from '@if/mui'
import { Stack } from '@mui/material'
import { access } from '@src/access'
import { useDeleteCoapplicant } from '@src/data/api/credit-api/credit-api'
import { mustShowInterestRate } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { Pencil, Trash2, UserPlus } from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  hasPendingAsyncChecks,
  hasPendingPrequalCheck,
  mustAskApplicantSIN,
  mustAskCoapplicantSIN,
} from '../../credit-hooks'
import { ECreditApplicationStatusMessage, GetPrequalStatus } from '../../viewCreditApp-selectors'
import ApplicationError from '../applicationError'
import PrequalNotStarted from '../PrequalSection/PrequalNotStarted'
import StageContent, { StageField } from './StageContent'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
}

// A soft-hit not consented behaves like a decline (same actions/message).
const isDeclinedLike = (status: ECreditApplicationStatusMessage | null, consentSoftHit: boolean) =>
  status === ECreditApplicationStatusMessage.Declined || !consentSoftHit

// Pre-qualification descriptor for StageContent: amounts + per-state message + actions.
const PrequalStageContent = ({ creditApp, merchant }: Props) => {
  const { t } = useTranslation()
  const showRate = useCan(access.showRate)
  const mustDisplayInterests = mustShowInterestRate(creditApp, merchant, showRate)
  const [deleteCoapplicant] = useDeleteCoapplicant()
  const confirmDeleteCoapp = useConfirmDialog({
    title: t('viewCreditApplication.deleteCoapplicant'),
    message: t('viewCreditApplication.confirmDeletionMessage'),
    mode: 'destructive',
    onConfirm: async () => {
      await deleteCoapplicant({ creditApplicationId: creditApp.id, financingProgramId: creditApp.financingProgramId })
    },
  })

  const { prequalificationDecision, requestedLoanAmount, id, financingProgramId, coApplicant, originSystemId } =
    creditApp
  // Approved maximum, capped by the requested amount.
  const maxLoanAmount =
    prequalificationDecision?.maxLoanAmount != null && requestedLoanAmount
      ? Math.min(requestedLoanAmount, prequalificationDecision.maxLoanAmount)
      : null

  const status = GetPrequalStatus(creditApp).status
  const isMerchDb = originSystemId === EOriginSystemId.MerchantDashboard

  // Early states where nothing is decided yet (drive the message-only view).
  const isDraft = creditApp.status === ECreditApplicationStatus.Draft
  const isPrequalRunning = hasPendingPrequalCheck(creditApp)

  // Amounts are hidden while there is nothing decided yet (draft / analysing).
  const fields: StageField[] =
    isDraft || isPrequalRunning
      ? []
      : [
          ...(mustDisplayInterests && prequalificationDecision != null
            ? [
                {
                  label: `${t('viewCreditApplication.interestRate')} (${t('common.regular')})`,
                  value: `${prequalificationDecision.minInterestRate} - ${prequalificationDecision.maxInterestRate}%`,
                },
              ]
            : []),
          { label: t('viewCreditApplication.amountRequested'), value: formatCurrency(requestedLoanAmount) },
          {
            label: t('worksheet.maxLoanAmount'),
            value: maxLoanAmount != null ? formatCurrency(maxLoanAmount) : undefined,
          },
        ]

  // The state's message shown above the amounts.
  const renderMessage = (): ReactNode => {
    if (isDraft) return <PrequalNotStarted />
    if (
      creditApp.status === ECreditApplicationStatus.Cancelled &&
      prequalificationDecision?.decision !== EDecision.Approved
    ) {
      return <Banner severity="error">{t('viewCreditApplication.creditAppCancelled')}</Banner>
    }
    if (status === ECreditApplicationStatusMessage.Error) return <ApplicationError />
    if (status === ECreditApplicationStatusMessage.SinRequired) {
      return (
        <Stack spacing={1}>
          {mustAskApplicantSIN(creditApp) && (
            <Banner severity="warning">{t('viewCreditApplication.applicantNewToCreditWarning')}</Banner>
          )}
          {mustAskCoapplicantSIN(creditApp) && (
            <Banner severity="warning">{t('viewCreditApplication.coapplicantNewToCreditWarning')}</Banner>
          )}
        </Stack>
      )
    }
    return undefined
  }

  // The stage's possible action buttons (picked per state in buildActions).
  const updateApplicant: Action = {
    icon: Pencil,
    label: t('viewCreditApplication.updateApplicant'),
    href: `/credits/${financingProgramId}/${id}/edit#applicant`,
  }
  const changeCoapp: Action = {
    icon: UserPlus,
    label: coApplicant ? t('viewCreditApplication.changeCoapp') : t('viewCreditApplication.addCoapp'),
    href: `/credits/${financingProgramId}/${id}/edit?addCoApp=true#coapplicant`,
    disabled: creditApp.finalCreditDecision?.isHardDecline,
  }
  const updateCoapp: Action = {
    icon: Pencil,
    label: t('viewCreditApplication.updateCoapplicant'),
    href: `/credits/${financingProgramId}/${id}/edit#coapplicant`,
  }
  const deleteCoapp: Action = {
    icon: Trash2,
    label: t('viewCreditApplication.deleteCoapplicant'),
    tone: 'destructive',
    onClick: () => void confirmDeleteCoapp.open(),
  }

  // Which actions per prequal state (same conditions as the former sub-components).
  const buildActions = (): Action[] => {
    if (!isMerchDb) return []
    if (status === ECreditApplicationStatusMessage.CreditReportNotFound) {
      // Co-applicant's report missing → mockup: Update + Remove co-applicant.
      if (creditApp.applicant.hasCreditReport && coApplicant?.hasCreditReport === false) {
        return [updateCoapp, deleteCoapp]
      }
      const list: Action[] = [updateApplicant]
      if (!coApplicant || !coApplicant.hasCreditReport) list.push(changeCoapp)
      return list
    }
    if (status === ECreditApplicationStatusMessage.SinRequired) {
      return mustAskCoapplicantSIN(creditApp) ? [updateApplicant, deleteCoapp] : [updateApplicant]
    }
    if (isDeclinedLike(status, creditApp.consentSoftHit)) return [changeCoapp]
    return []
  }

  const actions = buildActions()

  return (
    <StageContent
      fields={fields}
      loading={isPrequalRunning}
      pendingChecks={hasPendingAsyncChecks(creditApp)}
      customContent={renderMessage()}
      actions={
        actions.length > 0 ? (
          <ActionList actions={actions} variant="button" menuLabel={t('viewCreditApplication.actions')} />
        ) : undefined
      }
    />
  )
}

export default PrequalStageContent
